import { useEffect, useMemo, useState } from "react";
import { Table, Spinner, Alert, Input, Card, CardHeader, CardBody, Progress, Badge, Row, Col } from "reactstrap";
import { saveAs } from "file-saver";
import FeatherIcon from "feather-icons-react";
import { getPortfolioReport, getPadDashboard, exportPortfolioExcel } from "../TenantForm/Helpers/api";
import { DOCUMENT_TYPES } from "./ReportModal";
import ReportTabs from "./ReportTabs";
import RenewalsTab from "./RenewalsTab";
import { categoryMeta, money as padMoney, nextMonthYm, formatMonthLabel } from "../TenantForm/Helpers/PadInfo/padInfoTypes";

const money = (n) =>
  n === null || n === undefined
    ? "—"
    : new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(n) || 0);

const day = (v) => (v ? String(v).slice(0, 10) : "—");
const today = () => new Date();
const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

/** Document status for one portfolio row + one doc type — mirrors the per-property report. */
const docStatus = (row, docType) => {
  const count = (row.docs || {})[docType.document_type] || 0;
  if (count > 0) return { status: "Uploaded", count };
  if (row.documents_na && row.documents_na[docType.id] === true) return { status: "N/A", count: 0 };
  return { status: "Missing", count: 0 };
};

const REQUIRED = () => DOCUMENT_TYPES.filter((d) => d.alwaysVisible);

const missingRequired = (row) => REQUIRED().filter((d) => docStatus(row, d).status === "Missing");
const insuranceExpired = (row) => row.insurance_expiry && new Date(row.insurance_expiry) < today();

// The same tones the per-property Vacancy & Expiry tab paints its stat cards with, so
// both reports read as one product.
const TONE = {
  neutral: { bg: "#ffffff", border: "#e2e6ea", accent: "#334155" },
  amber: { bg: "#fff8e6", border: "#f5d990", accent: "#9a6700" },
  red: { bg: "#fdeaea", border: "#f3b6b6", accent: "#b3261e" },
  orange: { bg: "#fff1e6", border: "#f6c89a", accent: "#a8530c" },
  blue: { bg: "#e8f0fe", border: "#aac4f5", accent: "#1a56c4" },
  green: { bg: "#e6f4ea", border: "#7bc996", accent: "#1e7e45" },
};

const StatCard = ({ label, value, sub, tone = "neutral", onClick, active = false }) => {
  const t = TONE[tone] || TONE.neutral;
  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
      title={onClick ? `Show ${label}` : undefined}
      style={{
        flex: "1 1 132px",
        minWidth: 132,
        border: `1px solid ${active ? t.accent : t.border}`,
        boxShadow: active ? `inset 0 0 0 1px ${t.accent}` : undefined,
        background: t.bg,
        borderRadius: 8,
        padding: "8px 11px",
        cursor: onClick ? "pointer" : undefined,
      }}
    >
      <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}
      </div>
      <div style={{ fontSize: "1.2rem", fontWeight: 700, color: t.accent, lineHeight: 1.25, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
      {sub ? <div style={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div> : null}
    </div>
  );
};

/** A 3px bar under a table number — the per-property grid's density trick. */
const Meter = ({ value, color = "#0d6efd" }) => (
  <div style={{ height: 3, background: "#e9edf3", borderRadius: 2, marginTop: 3, overflow: "hidden" }}>
    <div style={{ width: `${Math.max(0, Math.min(100, value))}%`, height: "100%", background: color }} />
  </div>
);

const Chip = ({ tone = "neutral", children, title }) => {
  const t = TONE[tone] || TONE.neutral;
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: t.bg,
        color: t.accent,
        border: `1px solid ${t.border}`,
        borderRadius: 999,
        padding: "3px 10px",
        fontSize: "0.72rem",
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
};

const SectionTitle = ({ children, right }) => (
  <div className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
    <h6 className="fw-bold mb-0">{children}</h6>
    {right}
  </div>
);

const TABS = [
  { id: "overview", label: "Overview", icon: "pie-chart" },
  { id: "renewals", label: "Renewals", icon: "refresh-cw" },
  { id: "detailed", label: "Detailed", icon: "list" },
  { id: "sdVsRent", label: "SD vs Rent", icon: "dollar-sign" },
  { id: "utilities", label: "Utility Accounts", icon: "zap" },
  { id: "insurance", label: "Insurance", icon: "shield" },
  { id: "pad", label: "PAD", icon: "repeat" },
];

/**
 * The Report modal in All-Properties mode: the same seven tabs, wearing the same skin
 * as a single property's report (primary tab strip, stat cards, `table-sm` tables with
 * a light head and danger/warning row tints), computed across every property in the
 * viewer's scope from one server payload — documents counted server-side in a single
 * aggregation, no per-unit request storm. The PAD tab reads the scoped PAD dashboard
 * endpoint for its month numbers.
 */
const AllPropertiesReport = ({ onTitleChange }) => {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [padMonth, setPadMonth] = useState(() => nextMonthYm());
  const [pad, setPad] = useState(null);
  const [padLoading, setPadLoading] = useState(false);
  const [padError, setPadError] = useState(null);

  useEffect(() => {
    let active = true;
    getPortfolioReport()
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load");
        setData(resp.data);
      })
      .catch((err) => active && setError(err?.response?.data?.message || err.message || "Failed to load the report"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (tab !== "pad") return undefined;
    let active = true;
    setPadLoading(true);
    setPadError(null);
    getPadDashboard(padMonth)
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load");
        setPad(resp.data);
      })
      .catch((err) => active && setPadError(err?.response?.data?.message || err.message || "Failed to load PAD numbers"))
      .finally(() => active && setPadLoading(false));
    return () => { active = false; };
  }, [tab, padMonth]);

  useEffect(() => {
    if (!onTitleChange) return;
    const label = TABS.find((t) => t.id === tab)?.label || "Report";
    onTitleChange(`${label} · All Properties`);
  }, [tab, onTitleChange]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const properties = data?.properties || [];

  const [detailProblemsOnly, setDetailProblemsOnly] = useState(false);
  const [detailProperty, setDetailProperty] = useState("");
  const [expiryDays, setExpiryDays] = useState(90);
  const [segment, setSegment] = useState("attention");

  const handleExcelExport = async () => {
    setDownloading(true);
    try {
      const blob = await exportPortfolioExcel();
      saveAs(new Blob([blob]), "Portfolio_MasterSheet.xlsx");
    } catch (e) {
      console.error("Portfolio export failed:", e);
    } finally {
      setDownloading(false);
    }
  };

  // The tab strip renders before the data does, so a slow payload still looks like the
  // per-property report rather than a bare spinner on an empty modal.
  const tabStrip = (
    <ReportTabs
      tabs={TABS}
      active={tab}
      onChange={setTab}
      actions={
        <button
          type="button"
          className="rpt-action rpt-action--primary"
          onClick={handleExcelExport}
          disabled={downloading || loading || !rows.length}
        >
          {downloading ? <Spinner size="sm" /> : <><FeatherIcon icon="download" size={14} /> Download Excel</>}
        </button>
      }
    />
  );

  const body = () => {
    if (loading) return <div className="text-center p-4"><Spinner color="primary" /> Loading all properties…</div>;
    if (error) return <Alert color="danger" className="m-3">{error}</Alert>;
    if (!rows.length) return <Alert color="warning" className="m-3">No mastersheet units found for the properties assigned to you.</Alert>;
    return (
      <div className="p-4">
        {tab === "overview" && renderOverview()}
        {tab === "renewals" && <RenewalsTab rows={rows} />}
        {tab === "detailed" && renderDetailed()}
        {tab === "sdVsRent" && renderSdVsRent()}
        {tab === "utilities" && renderUtilities()}
        {tab === "insurance" && renderInsurance()}
        {tab === "pad" && renderPad()}
      </div>
    );
  };

  const tableProps = { hover: true, responsive: true, className: "table-sm align-middle mb-0" };

  // Every number both the old Summary and the old Vacancy & Expiry tabs computed,
  // derived once per unit so the KPI band, the per-property grid and the attention
  // list can never disagree with each other.
  const overview = useMemo(() => {
    const win = Math.max(1, parseInt(expiryDays, 10) || 90);
    const now = today();
    const requiredCount = REQUIRED().length;

    const units = rows.map((r) => {
      const miss = missingRequired(r);
      const end = r.lease_end ? new Date(r.lease_end) : null;
      const daysLeft = end && !isNaN(end.getTime()) ? Math.ceil((end - now) / 86400000) : null;
      const vacant = !r.tenant;
      const insExpired = !!insuranceExpired(r);
      const insMissing = !r.insurance_expiry;
      const ended = !vacant && daysLeft !== null && daysLeft < 0;
      const expiring = !vacant && daysLeft !== null && daysLeft >= 0 && daysLeft <= win;

      const issues = [];
      if (ended) issues.push({ key: "ended", label: `Ended ${Math.abs(daysLeft)}d ago`, tone: "red" });
      if (vacant) issues.push({ key: "vacant", label: "Vacant", tone: "amber" });
      if (expiring) {
        issues.push({
          key: "expiring",
          label: daysLeft <= 30 ? "Ends ≤ 30 days" : daysLeft <= 60 ? "Ends 31–60 days" : `Ends in ${daysLeft} days`,
          tone: daysLeft <= 30 ? "red" : daysLeft <= 60 ? "orange" : "blue",
        });
      }
      if (insExpired) issues.push({ key: "insurance", label: "Insurance expired", tone: "red" });
      if (miss.length) issues.push({ key: "docs", label: `${miss.length} doc${miss.length === 1 ? "" : "s"} missing`, tone: "amber" });

      // Severity, so the default list opens on what actually needs doing today.
      const rank = ended ? 0
        : expiring && daysLeft <= 30 ? 1
          : vacant ? 2
            : insExpired ? 3
              : expiring ? 4
                : miss.length ? 5 : 9;

      return {
        row: r, miss, requiredCount, daysLeft, vacant, insExpired, insMissing, ended, expiring,
        issues, rank, complete: miss.length === 0 && !insExpired,
      };
    });

    const t = { units: units.length, occupied: 0, vacant: 0, complete: 0, missingUnits: 0, insExpired: 0, insMissing: 0, expiring: 0, expiring30: 0, ended: 0 };
    const byProperty = new Map();

    for (const u of units) {
      const r = u.row;
      let p = byProperty.get(r.property_id);
      if (!p) {
        p = { property_id: r.property_id, property_name: r.property_name, units: 0, occupied: 0, vacant: 0, complete: 0, missingUnits: 0, insExpired: 0, insMissing: 0, expiring: 0, expiring30: 0, ended: 0 };
        byProperty.set(r.property_id, p);
      }
      const bump = (k) => { t[k] += 1; p[k] += 1; };
      p.units += 1;
      if (u.vacant) bump("vacant"); else bump("occupied");
      if (u.complete) bump("complete");
      if (u.miss.length) bump("missingUnits");
      if (u.insExpired) bump("insExpired");
      if (u.insMissing) bump("insMissing");
      if (u.expiring) { bump("expiring"); if (u.daysLeft <= 30) bump("expiring30"); }
      if (u.ended) bump("ended");
    }

    const properties = [...byProperty.values()].sort((a, b) => a.property_name.localeCompare(b.property_name));
    const attention = units.filter((u) => u.rank < 9);
    return { win, units, totals: t, properties, attention };
  }, [rows, expiryDays]);

  // One list, six lenses. "Needs attention" is every flagged unit at once — the
  // vacant units and the expiring leases that used to sit in two separate tables.
  const SEGMENTS = [
    { id: "attention", label: "Needs attention", count: overview.attention.length, test: () => true },
    { id: "vacant", label: "Vacant", count: overview.totals.vacant, test: (u) => u.vacant },
    { id: "expiring", label: `Ends ≤ ${overview.win}d`, count: overview.totals.expiring, test: (u) => u.expiring },
    { id: "ended", label: "Already ended", count: overview.totals.ended, test: (u) => u.ended },
    { id: "docs", label: "Docs missing", count: overview.totals.missingUnits, test: (u) => u.miss.length > 0 },
    { id: "insurance", label: "Insurance expired", count: overview.totals.insExpired, test: (u) => u.insExpired },
    { id: "all", label: "All units", count: overview.units.length, test: () => true },
  ];

  const renderOverview = () => {
    const t = overview.totals;
    const active = SEGMENTS.find((sg) => sg.id === segment) || SEGMENTS[0];
    const pool = segment === "all" ? overview.units : segment === "attention" ? overview.attention : overview.units.filter(active.test);
    const listed = [...pool].sort(
      (a, b) =>
        a.rank - b.rank ||
        (a.daysLeft ?? Number.POSITIVE_INFINITY) - (b.daysLeft ?? Number.POSITIVE_INFINITY) ||
        a.row.property_name.localeCompare(b.row.property_name) ||
        String(a.row.unit_no).localeCompare(String(b.row.unit_no), undefined, { numeric: true })
    );

    const jump = (id) => setSegment((prev) => (prev === id ? "attention" : id));

    return (
      <>
        {/* ── Portfolio health: the old Summary card, plus the vacancy/expiry
             headline numbers, in one band ── */}
        <Card className="mb-3">
          <CardHeader className="bg-primary text-white d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
            <span>Portfolio Overview — {t.units} unit{t.units === 1 ? "" : "s"} across {overview.properties.length} propert{overview.properties.length === 1 ? "y" : "ies"}</span>
            <span className="ms-auto d-inline-flex align-items-center" style={{ gap: 6, fontSize: "0.8rem", fontWeight: 500 }}>
              Lease window
              <Input
                type="number"
                bsSize="sm"
                min="1"
                style={{ width: 76 }}
                value={expiryDays}
                aria-label="Expiry window days"
                onChange={(e) => setExpiryDays(e.target.value)}
              />
              days
            </span>
          </CardHeader>
          <CardBody className="pb-2">
            <div className="d-flex flex-wrap gap-2 mb-3">
              <StatCard label="Units" value={t.units} sub={`${overview.properties.length} properties`} />
              <StatCard label="Occupied" value={`${t.occupied} (${pct(t.occupied, t.units)}%)`} tone="green" />
              <StatCard label="Vacant" value={t.vacant} tone={t.vacant ? "amber" : "neutral"} onClick={() => jump("vacant")} active={segment === "vacant"} />
              <StatCard label={`Ends ≤ ${overview.win}d`} value={t.expiring} sub={`${t.expiring30} within 30d`} tone={t.expiring30 ? "red" : t.expiring ? "blue" : "neutral"} onClick={() => jump("expiring")} active={segment === "expiring"} />
              <StatCard label="Already ended" value={t.ended} tone={t.ended ? "red" : "neutral"} onClick={() => jump("ended")} active={segment === "ended"} />
              <StatCard label="Docs complete" value={`${t.complete} (${pct(t.complete, t.units)}%)`} tone="green" />
              <StatCard label="Docs missing" value={t.missingUnits} tone={t.missingUnits ? "amber" : "neutral"} onClick={() => jump("docs")} active={segment === "docs"} />
              <StatCard label="Insurance expired" value={t.insExpired} sub={`${t.insMissing} with no date`} tone={t.insExpired ? "red" : "neutral"} onClick={() => jump("insurance")} active={segment === "insurance"} />
            </div>

            <Row className="g-3">
              <Col md={4}>
                <p className="mb-1" style={{ fontSize: 13 }}>
                  <strong>With Tenant:</strong> {t.occupied} ({pct(t.occupied, t.units)}%)
                </p>
                <Progress value={pct(t.occupied, t.units)} color="info" style={{ height: 8 }} />
              </Col>
              <Col md={4}>
                <p className="mb-1 d-flex justify-content-between align-items-center" style={{ fontSize: 13 }}>
                  <span><strong>Without Insurance Date:</strong> {t.insMissing} ({pct(t.insMissing, t.units)}%)</span>
                  <span style={{ color: "red", fontSize: "0.85em" }}>{t.insExpired} expired</span>
                </p>
                <Progress value={pct(t.insMissing, t.units)} color="warning" style={{ height: 8 }} />
              </Col>
              <Col md={4}>
                <p className="mb-1" style={{ fontSize: 13 }}>
                  <strong>All Required Docs:</strong> {t.complete} ({pct(t.complete, t.units)}%)
                </p>
                <Progress value={pct(t.complete, t.units)} color="success" style={{ height: 8 }} />
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ── Per-property grid: document health and lease pressure on one line
             per property, so neither report needs its own table any more ── */}
        <SectionTitle>Per-property breakdown</SectionTitle>
        <Table {...tableProps}>
          <thead className="table-light">
            <tr>
              <th>Property</th>
              <th className="text-end">Units</th>
              <th className="text-end">Occupied</th>
              <th className="text-end">Vacant</th>
              <th className="text-end">Docs complete</th>
              <th className="text-end">Missing required docs</th>
              <th className="text-end">Insurance expired</th>
              <th className="text-end">No insurance date</th>
              <th className="text-end">Ends ≤ 30d</th>
              <th className="text-end">Ends ≤ {overview.win}d</th>
            </tr>
          </thead>
          <tbody>
            {overview.properties.map((p) => (
              <tr key={p.property_id} className={p.missingUnits || p.insExpired || p.expiring30 || p.ended ? "table-warning" : undefined}>
                <td><strong>{p.property_name}</strong></td>
                <td className="text-end">{p.units}</td>
                <td className="text-end">
                  {p.occupied} ({pct(p.occupied, p.units)}%)
                  <Meter value={pct(p.occupied, p.units)} color="#0dcaf0" />
                </td>
                <td className="text-end">{p.vacant || 0}</td>
                <td className="text-end">
                  {p.complete} ({pct(p.complete, p.units)}%)
                  <Meter value={pct(p.complete, p.units)} color="#198754" />
                </td>
                <td className="text-end">{p.missingUnits ? <Badge color="danger">{p.missingUnits}</Badge> : 0}</td>
                <td className="text-end">{p.insExpired ? <Badge color="danger">{p.insExpired}</Badge> : 0}</td>
                <td className="text-end">{p.insMissing || 0}</td>
                <td className="text-end">{p.expiring30 ? <Badge color="danger">{p.expiring30}</Badge> : 0}</td>
                <td className="text-end">{p.expiring || 0}</td>
              </tr>
            ))}
            <tr className="table-light fw-bold">
              <td>All properties</td>
              <td className="text-end">{t.units}</td>
              <td className="text-end">{t.occupied}</td>
              <td className="text-end">{t.vacant}</td>
              <td className="text-end">{t.complete}</td>
              <td className="text-end">{t.missingUnits}</td>
              <td className="text-end">{t.insExpired}</td>
              <td className="text-end">{t.insMissing}</td>
              <td className="text-end">{t.expiring30}</td>
              <td className="text-end">{t.expiring}</td>
            </tr>
          </tbody>
        </Table>

        {/* ── The unit-level worklist that replaces the two vacancy tables ── */}
        <div className="mt-4">
          <SectionTitle
            right={<span className="text-muted" style={{ fontSize: 12 }}>{listed.length} unit{listed.length === 1 ? "" : "s"}</span>}
          >
            {active.label} · {listed.length}
          </SectionTitle>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {SEGMENTS.map((sg) => (
              <button
                key={sg.id}
                type="button"
                className={`btn btn-sm ${segment === sg.id ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setSegment(sg.id)}
              >
                {sg.label} <span className="opacity-75">· {sg.count}</span>
              </button>
            ))}
          </div>
          <Table {...tableProps}>
            <thead className="table-light">
              <tr>
                <th>Property</th>
                <th>Unit #</th>
                <th>Tenant</th>
                <th>Lease End</th>
                <th className="text-end">Rent</th>
                <th className="text-end">Docs</th>
                <th>Insurance</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {listed.length ? listed.map((u) => {
                const r = u.row;
                const rowCls = u.ended || u.insExpired || (u.expiring && u.daysLeft <= 30)
                  ? "table-danger"
                  : u.vacant ? "table-warning" : undefined;
                return (
                  <tr key={r.item_id} className={rowCls}>
                    <td>{r.property_name}{r.building_name ? ` · ${r.building_name}` : ""}</td>
                    <td><strong>{r.unit_no}</strong></td>
                    <td>{r.tenant || <em className="text-muted">vacant</em>}</td>
                    <td>
                      {r.lease_end ? day(r.lease_end) : <span className="text-muted">—</span>}
                      {u.daysLeft !== null && (
                        <div style={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 600 }}>
                          {u.daysLeft < 0 ? `${Math.abs(u.daysLeft)}d ago` : `in ${u.daysLeft}d`}
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      {u.vacant ? money(r.target_rent) : money(r.basic_rent)}
                      {u.vacant && <div style={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 600 }}>target</div>}
                    </td>
                    <td className="text-end" title={u.miss.map((d) => d.title).join(", ")}>
                      {u.requiredCount - u.miss.length}/{u.requiredCount}
                    </td>
                    <td>{r.insurance_expiry ? day(r.insurance_expiry) : <span className="text-muted">—</span>}</td>
                    <td>
                      <span className="d-inline-flex flex-wrap" style={{ gap: 4 }}>
                        {u.issues.length
                          ? u.issues.map((i) => <Chip key={i.key} tone={i.tone}>{i.label}</Chip>)
                          : <Chip tone="green">All clear</Chip>}
                      </span>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan={8} className="text-center text-muted">Nothing in this view — everything here is in order.</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      </>
    );
  };

  const renderDetailed = () => {
    const scoped = detailProperty ? rows.filter((r) => r.property_id === detailProperty) : rows;
    const shown = detailProblemsOnly ? scoped.filter((r) => missingRequired(r).length > 0 || insuranceExpired(r)) : scoped;
    const problems = scoped.filter((r) => missingRequired(r).length > 0 || insuranceExpired(r)).length;
    return (
      <div>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <StatCard label="Units" value={scoped.length} />
          <StatCard label="Clean" value={scoped.length - problems} tone="green" />
          <StatCard label="Need attention" value={problems} tone={problems ? "red" : "neutral"} sub="missing docs or expired insurance" />
        </div>

        <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
          <div className="form-check mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="allDetailProblemsOnly"
              checked={detailProblemsOnly}
              onChange={(e) => setDetailProblemsOnly(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="allDetailProblemsOnly">
              Only units with problems
            </label>
          </div>
          <Input
            type="select"
            bsSize="sm"
            style={{ width: 200 }}
            value={detailProperty}
            aria-label="Filter detailed report by property"
            onChange={(e) => setDetailProperty(e.target.value)}
          >
            <option value="">All properties</option>
            {properties.map((p) => (
              <option key={p.property_id} value={p.property_id}>{p.property_name}</option>
            ))}
          </Input>
          <span className="ms-auto text-muted" style={{ fontSize: 12 }}>{shown.length} shown</span>
        </div>

        <Table {...tableProps}>
          <thead className="table-light">
            <tr>
              <th>Property</th>
              <th>Unit #</th>
              <th>Tenant</th>
              <th className="text-end">Docs</th>
              <th className="text-end">Missing required</th>
              <th>Insurance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.length ? shown.map((r) => {
              const miss = missingRequired(r);
              const totalDocs = Object.values(r.docs || {}).reduce((s, n) => s + n, 0);
              const insBad = insuranceExpired(r);
              return (
                <tr key={r.item_id} className={miss.length || insBad ? "table-danger" : "table-success"}>
                  <td>{r.property_name}{r.building_name ? ` · ${r.building_name}` : ""}</td>
                  <td><strong>{r.unit_no}</strong></td>
                  <td>{r.tenant || <em className="text-muted">vacant</em>}</td>
                  <td className="text-end">{totalDocs}</td>
                  <td className="text-end" title={miss.map((d) => d.title).join(", ")}>
                    {miss.length ? <Badge color="danger">{miss.length}</Badge> : "—"}
                  </td>
                  <td>{day(r.insurance_expiry)}</td>
                  <td>
                    {insBad
                      ? <Badge color="danger">Insurance expired</Badge>
                      : miss.length
                        ? <Badge color="warning" className="text-dark">Docs missing</Badge>
                        : <Badge color="success">Complete</Badge>}
                  </td>
                </tr>
              );
            }) : (
              <tr><td colSpan={7} className="text-center text-muted">No units to display.</td></tr>
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderSdVsRent = () => {
    const leased = rows.filter((r) => r.tenant);
    const assessed = leased.map((r) => {
      const has = r.basic_rent !== null && r.sd !== null;
      const diff = has ? r.sd - r.basic_rent : null;
      return { row: r, has, diff, over: has && diff > 0 };
    });
    const over = assessed.filter((a) => a.over);
    const owed = over.reduce((s, a) => s + a.diff, 0);
    return (
      <div>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <StatCard label="Units assessed" value={leased.length} />
          <StatCard label="Over-collected units" value={over.length} sub={`${money(owed)} owed`} tone={over.length ? "red" : "neutral"} />
          <StatCard label="Compliant units" value={assessed.filter((a) => a.has && !a.over).length} tone="green" />
          <StatCard label="No data on file" value={assessed.filter((a) => !a.has).length} tone="amber" />
        </div>
        <Table {...tableProps}>
          <thead className="table-light">
            <tr>
              <th>Property</th>
              <th>Unit #</th>
              <th>Tenant</th>
              <th className="text-end">Basic Rent</th>
              <th className="text-end">Security Deposit</th>
              <th className="text-end">Difference</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {assessed.length ? assessed.map(({ row: r, has, diff, over: isOver }) => (
              <tr key={r.item_id} className={isOver ? "table-danger" : undefined}>
                <td>{r.property_name}</td>
                <td><strong>{r.unit_no}</strong></td>
                <td>{r.tenant}</td>
                <td className="text-end">{money(r.basic_rent)}</td>
                <td className="text-end">{money(r.sd)}</td>
                <td className="text-end">{diff === null ? "—" : money(diff)}</td>
                <td>
                  <Badge color={!has ? "warning" : isOver ? "danger" : "success"} className={!has ? "text-dark" : undefined}>
                    {!has ? "No data" : isOver ? "Over-collected" : "Compliant"}
                  </Badge>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={7} className="text-center text-muted">No leased units to display.</td></tr>
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderUtilities = () => {
    const leased = rows.filter((r) => r.tenant);
    const account = (v) => (v ? <span>{v}</span> : <Badge color="secondary">Missing</Badge>);
    const missing = (key) => leased.filter((r) => !r.utilities?.[key]).length;
    return (
      <div>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <StatCard label="Leased units" value={leased.length} />
          <StatCard label="No heat account" value={missing("heat")} tone={missing("heat") ? "amber" : "green"} />
          <StatCard label="No water account" value={missing("water")} tone={missing("water") ? "amber" : "green"} />
          <StatCard label="No power account" value={missing("electricity")} tone={missing("electricity") ? "amber" : "green"} />
        </div>
        <Table {...tableProps}>
          <thead className="table-light">
            <tr><th>Property</th><th>Unit #</th><th>Tenant</th><th>Heat</th><th>Water</th><th>Electricity</th></tr>
          </thead>
          <tbody>
            {leased.length ? leased.map((r) => (
              <tr key={r.item_id}>
                <td>{r.property_name}</td>
                <td><strong>{r.unit_no}</strong></td>
                <td>{r.tenant}</td>
                <td>{account(r.utilities?.heat)}</td>
                <td>{account(r.utilities?.water)}</td>
                <td>{account(r.utilities?.electricity)}</td>
              </tr>
            )) : (
              <tr><td colSpan={6} className="text-center text-muted">No leased units to display.</td></tr>
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderInsurance = () => {
    const soon = new Date(Date.now() + 30 * 86400000);
    const leased = rows.filter((r) => r.tenant);
    const expired = leased.filter((r) => insuranceExpired(r));
    const expiring = leased.filter((r) => r.insurance_expiry && !insuranceExpired(r) && new Date(r.insurance_expiry) <= soon);
    const noDate = leased.filter((r) => !r.insurance_expiry);
    const valid = leased.length - expired.length - expiring.length - noDate.length;

    const buckets = [
      { label: "Expired", rows: expired, cls: "table-danger", color: "danger" },
      { label: "Expiring within 30 days", rows: expiring, cls: "table-warning", color: "warning" },
      { label: "No insurance date on file", rows: noDate, cls: "", color: "secondary" },
    ];

    return (
      <div>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Badge color="danger">Expired: {expired.length}</Badge>
          <Badge color="warning" className="text-dark">Expiring ≤ 30d: {expiring.length}</Badge>
          <Badge color="success">Valid: {valid}</Badge>
          <Badge color="secondary">No date: {noDate.length}</Badge>
          <Badge color="info" className="text-dark">Units: {leased.length}</Badge>
        </div>

        {buckets.map((b) => (
          <div key={b.label} className="mb-4">
            <SectionTitle>{b.label} · {b.rows.length}</SectionTitle>
            <Table {...tableProps}>
              <thead className="table-light">
                <tr><th>Property</th><th>Unit #</th><th>Tenant</th><th>Insurance Expiry</th><th>Status</th></tr>
              </thead>
              <tbody>
                {b.rows.length ? b.rows.map((r) => (
                  <tr key={r.item_id} className={b.cls || undefined}>
                    <td>{r.property_name}</td>
                    <td><strong>{r.unit_no}</strong></td>
                    <td>{r.tenant}</td>
                    <td>{day(r.insurance_expiry)}</td>
                    <td><Badge color={b.color} className={b.color === "warning" ? "text-dark" : undefined}>{b.label}</Badge></td>
                  </tr>
                )) : (
                  <tr><td colSpan={5} className="text-center text-muted">Nothing in this bucket.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        ))}
      </div>
    );
  };

  const renderPad = () => {
    if (padLoading) return <div className="text-center p-4"><Spinner color="primary" /> Loading PAD numbers…</div>;
    if (padError) return <Alert color="danger">{padError}</Alert>;
    const props = pad?.properties || [];
    const t = pad?.totals;
    return (
      <div>
        <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
          <Input type="month" bsSize="sm" style={{ width: 170 }} value={padMonth} aria-label="PAD month" onChange={(e) => e.target.value && setPadMonth(e.target.value)} />
          <span style={{ fontSize: 12.5, fontWeight: 600, color: "#334155" }}>{formatMonthLabel(padMonth)}</span>
        </div>

        {t && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            <StatCard label={`${categoryMeta("NEW").label}s`} value={t.new_pads || 0} tone="green" />
            <StatCard label="Incentives" value={padMoney(t.incentives_total)} sub={`${t.incentives_count || 0} unit${t.incentives_count === 1 ? "" : "s"}`} tone="blue" />
            <StatCard label="Delays" value={`${t.delays} delay${t.delays === 1 ? "" : "s"}`} tone={t.delays ? "amber" : "neutral"} />
            <StatCard label="Temp stops" value={t.temp_stops || 0} tone={t.temp_stops ? "orange" : "neutral"} />
            <StatCard label="Skipped pulls" value={t.skipped_pulls || 0} tone={t.skipped_pulls ? "red" : "neutral"} />
          </div>
        )}

        <Table {...tableProps}>
          <thead className="table-light">
            <tr>
              <th>Property</th>
              <th className="text-end">{categoryMeta("NEW").label}</th>
              <th className="text-end">{categoryMeta("DELAY").label}s</th>
              <th className="text-end">Temp stops</th>
              <th className="text-end">Skipped pulls</th>
              <th className="text-end">Incentives</th>
            </tr>
          </thead>
          <tbody>
            {props.length ? props.map((p) => (
              <tr key={p.property_id}>
                <td><strong>{p.property_name}</strong></td>
                <td className="text-end">{p.counts?.NEW || 0}</td>
                <td className="text-end">{p.delays || 0}</td>
                <td className="text-end">{p.temp_stops || 0}</td>
                <td className="text-end">{p.skipped_pulls || 0}</td>
                <td className="text-end">{padMoney(p.incentives_total || 0)}</td>
              </tr>
            )) : (
              <tr><td colSpan={6} className="text-center text-muted">Nothing recorded for this month.</td></tr>
            )}
          </tbody>
        </Table>
        <p className="text-muted mt-2 mb-0" style={{ fontSize: 12 }}>
          Open a property's own Report → PAD tab for the full unit-by-unit month report, verification, and Excel.
        </p>
      </div>
    );
  };

  return (
    <>
      {tabStrip}
      {body()}
    </>
  );
};

export default AllPropertiesReport;
